<template>
  <div class="page lightmap">
    <navigation-header />
    <vertical-stack class="main">
      <prompt-reset-password
        v-if="state === ViewState.PromptResetPassword"
        @skip="setViewState(nextState)"
      />
      <div v-else>
        <div v-if="state === ViewState.NewUserAuthenticated">
          <user-header />
        </div>
        <div v-else>
          <div v-if="isErrorState || isLoadingState" class="eyebrow-heading">
            {{ $t(`accept_invite.${state}.eyebrow`) }}
          </div>
          <div v-else class="icon">
            <add-contact-splash-icon :state="state === ViewState.AcceptInviteSuccess" />
          </div>

          <h2 v-if="te(`accept_invite.${state}.heading`)" class="heading-medium">
            {{ $t(`accept_invite.${state}.heading`) }}
          </h2>

          <heading-chip />
        </div>

        <paragraph-content :keypath="`accept_invite.${state}.paragraph`" :sendername="sendername" :email="email" />

        <paragraph-content
          v-if="te(`accept_invite.${state}.paragraph2`)" class="margin-top-small"
          :keypath="`accept_invite.${state}.paragraph2`" :sendername="sendername" :email="email"
        />

        <a
          v-if="isErrorState" class="paragraph-content margin-top-small" href="/"
          @click.prevent="redirectToHomeOrLogin()"
        >{{
          $t(`common.link.go_back_to_the_start`)
        }}
        </a>

        <vertical-stack v-if="showUserProfile" class="paragraph-content">
          <user-profile />

          <div>
            {{ $t(`accept_invite.${state}.profile_text`) }}
          </div>

          <a href="/" @click.prevent="onShowAccountDetailsModal">{{
            $t('links.see_more_account_details')
          }}</a>

          <user-details v-model="accountDetailsModalVisible" @close="onHideAccountDetailsModal" />
        </vertical-stack>

        <authenticator
          v-show="showAuthenticator" ref="authComponent" class="authenticator"
          @state-changed="onAuthStateChanged"
        />

        <vertical-stack v-if="showAcceptInviteButton" class="button-container">
          <large-button type="submit" @click="processAcceptedInvite">
            {{ $t('accept_invite.buttons.accept_invite') }}
          </large-button>
          <large-button v-if="showDifferentAccountButton" variant="secondary" @click="signOut()">
            {{ $t('accept_invite.buttons.use_a_different_account') }}
          </large-button>
        </vertical-stack>

        <vertical-stack v-if="state === ViewState.AcceptOwnInvite" class="button-container">
          <large-button variant="secondary" @click="signOut()">
            {{ $t('accept_invite.buttons.use_a_different_account') }}
          </large-button>
        </vertical-stack>

        <product-links v-if="state === ViewState.AcceptInviteSuccess" />

        <Loading v-if="isLoadingState" />
      </div>
    </vertical-stack>
    <accounts-footer />
  </div>
</template>

<script setup>
import { onMounted, watch, ref, computed, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
  Authenticator, useAuthentication, useInvitesApi, AvatarIcon,
  LargeButton, VerticalStack, AddContactSplashIcon,
  GooglePlayBadge, AppleStoreBadge, WebappBadge, Loading,
  Modal, UserDetails, AuthState, useAnalytics
}
  from 'ihunter-pathfinder-components';

import { InviteErrorCode } from 'ihunter-common';

import { useRouteManager } from '../composables/useRouteManager';
import { useTestState } from '../composables/useTestState';

import NavigationHeader from '../components/NavigationHeader.vue';
import UserHeader from '../components/UserHeader.vue';
import UserProfile from '../components/UserProfile.vue';
import ProductLinks from '../components/ProductLinks.vue';
import AccountsFooter from '../components/AccountsFooter.vue';
import HeadingChip from '../components/HeadingChip.vue';
import ParagraphContent from '../components/ParagraphContent.vue';
import PromptResetPassword from '../components/PromptResetPassword.vue';

const route = useRoute();
const { te } = useI18n();

const { user, signOut } = useAuthentication();
const { logScreenView, logInviteRequestAccepted, EventSource } = useAnalytics();
const { redirectToHomeOrLogin } = useRouteManager();
const { initializeTestStateFromUrl, TestStateUser, testState } = useTestState();

const ViewState = {
  // Invite valid but not accepted
  NotAuthenticated: 'not_authenticated',
  NewUserAuthenticated: 'new_user_authenticated',
  ExistingUserAuthenticated: 'existing_user_authenticated',
  ExistingUserAlreadyAuthenticated: 'existing_user_already_authenticated',

  // Authenticated but password reset is recommended
  PromptResetPassword: 'prompt_reset_password',

  // Invite accepted
  AcceptInviteSuccess: 'accept_invite_success',

  // Invite link resent
  AcceptInviteLinkResent: 'accept_invite_link_resent',

  // Progress states
  ValidatingInvite: 'validating_invite',
  AcceptingInvite: 'accepting_invite',

  // Error states
  InviteInvalid: 'invite_invalid',
  AcceptInviteFailed: 'accept_invite_failed',
  AcceptOwnInvite: 'accept_own_invite', // HUNT-2064 Signed in to the account that sent the invite
};
Object.freeze(ViewState);

const state = ref(ViewState.ValidatingInvite);
const nextState = ref(null);
const isInviteInitialized = ref(false);
const isUserInitialized = ref(false);
const authComponent = ref(null);
const accountDetailsModalVisible = ref(false);
const accountSummary = ref(null);
const email = ref(null);
const sendername = ref(null);

const showSpacer = ref(false);

const { callValidateInviteApi, callAcceptInviteApi } = useInvitesApi();

onMounted(async () => {
  console.log(`Initialize AcceptInviteView: invite=${route.query.inviteUid}, sendername=${route.query.senderName}, ${authComponent.value}`);

  initializeTestStateFromUrl(ViewState);
  if (testState.value) {
    setViewState(testState.value);
    email.value = TestStateUser.email;
    sendername.value = 'Fake Sender';
    if (testState.value === ViewState.PromptResetPassword) {
      nextState.value = ViewState.ExistingUserAuthenticated;
    }
  } else {
    await validateInvite();
  }
  initializeAuthComponent();
});

const validateInvite = async () => {
  try {
    setViewState(ViewState.ValidatingInvite);
    sendername.value = route.query.senderName;
    await callValidateInviteApi(route.query.inviteUid);
    console.log(`Invite is valid`);
    isInviteInitialized.value = true;
    await updateStateForUser();
  } catch (error) {
    setViewState(ViewState.InviteInvalid);
    console.error(`Invite invalid, set view state: ${state.value}`, error);
  }
}

const initializeAuthComponent = () => {
  if (showAuthenticator.value) {
    nextTick(() => { // ensure authComponent is mounted after state initialization
      let authState;
      if (testState.value && state.value === ViewState.AcceptInviteLinkResent) {
        authState = AuthState.AcceptInviteLinkResent;
        authComponent.value.setAuthEmail(email.value);
      } else if (!testState.value && !authComponent.value.state) {
        authState = user.value ? AuthState.SignOut : AuthState.SignIn;
      }

      if (authState) {
        console.log(`initializeAuthComponent: ${authState}`);
        authComponent.value.setAuthState(authState);
      }
    });
  }
}

watch(user, async () => {
  if (testState.value) {
    user.value = (state.value === ViewState.NotAuthenticated) ? null : user.value || TestStateUser;
  } else {
    await updateStateForUser();
  }
});

const updateStateForUser = async () => {
  const validatingInvite = state.value === ViewState.ValidatingInvite && !isInviteInitialized.value;
  const signedOutOfOwnInvite = state.value === ViewState.AcceptOwnInvite && !user.value;
  if ((isErrorState.value && !signedOutOfOwnInvite) || validatingInvite) {
    console.log(`Ignoring user updates in the ${state.value} state`);
    return;
  }

  const oldState = state.value;
  const previouslyUnauthenticated = isUserInitialized.value && state.value !== ViewState.ExistingUserAlreadyAuthenticated;

  if (!user.value) {
    setViewState(ViewState.NotAuthenticated);
  } else {
    if (previouslyUnauthenticated) {
      nextState.value = user.value.isNewUser ? ViewState.NewUserAuthenticated : ViewState.ExistingUserAuthenticated;
      setViewState(user.value.needsPasswordReset ? ViewState.PromptResetPassword : nextState.value);
    } else {
      setViewState(ViewState.ExistingUserAlreadyAuthenticated);
    }
  }
  isUserInitialized.value = true;
}

const onAuthStateChanged = async (authState, authEmail) => {
  if (!usingTestLinkResentState() && authState === AuthState.AcceptInviteLinkResent) {
    setViewState(ViewState.AcceptInviteLinkResent);
    email.value = authEmail;
    console.log(`The invite link was resent, view state: ${state.value}, email: ${email.value}`);
  }
};

const showUserProfile = computed(() => {
  return (
    state.value === ViewState.ExistingUserAuthenticated ||
    state.value === ViewState.ExistingUserAlreadyAuthenticated
  );
});

const showAuthenticator = computed(() => {
  console.log(`showAuthenticator ${state.value === ViewState.NotAuthenticated ||
    state.value == ViewState.AcceptInviteLinkResent} for state ${state.value}`);
  return state.value === ViewState.NotAuthenticated ||
    state.value == ViewState.AcceptInviteLinkResent;
});

const showAcceptInviteButton = computed(() => {
  return (
    state.value === ViewState.NewUserAuthenticated ||
    state.value === ViewState.ExistingUserAuthenticated ||
    state.value === ViewState.ExistingUserAlreadyAuthenticated
  );
});

const showDifferentAccountButton = computed(() => {
  return (
    state.value === ViewState.ExistingUserAuthenticated ||
    state.value === ViewState.ExistingUserAlreadyAuthenticated
  );
});

const isLoadingState = computed(() => {
  return state.value === ViewState.ValidatingInvite || state.value === ViewState.AcceptingInvite;
});

const isErrorState = computed(() => {
  return (
    state.value === ViewState.InviteInvalid ||
    state.value === ViewState.AcceptInviteFailed ||
    state.value === ViewState.AcceptOwnInvite
  );
});

const usingTestLinkResentState = () => {
  return testState.value && state.value === ViewState.AcceptInviteLinkResent;
};

const processAcceptedInvite = async () => {
  try {
    setViewState(ViewState.AcceptingInvite);
    const inviteUid = route.query.inviteUid;
    console.log(`Accepting invitation ${inviteUid} for email ${user.value.email}...`);
    await callAcceptInviteApi(inviteUid, user.value);
    setViewState(ViewState.AcceptInviteSuccess);
    logInviteRequestAccepted(EventSource.EmailLink);
  } catch (error) {
    if (error.code === InviteErrorCode.CannotAcceptOwnInvite) {
      console.warn(`processAcceptedInvite: signed in to the account that sent the invite`, error);
      email.value = user.value.email;
      setViewState(ViewState.AcceptOwnInvite);
    } else {
      console.error(`processAcceptedInvite`, error);
      setViewState(ViewState.AcceptInviteFailed);
    }
  }
};

const onShowAccountDetailsModal = async () => {
  accountDetailsModalVisible.value = true;
};

const onHideAccountDetailsModal = async () => {
  accountDetailsModalVisible.value = false;
};

const setViewState = (viewState) => {
  console.log(`Setting view state: ${viewState}, old state: ${state.value}, user: ${user.value?.uid}`);
  logScreenView(viewState);
  state.value = viewState;
}

</script>

<style lang="scss" scoped>
@use 'ihunter-pathfinder/styles' as Pathfinder;
@use '../styles/Accounts' as Accounts;

.page {
  @include Accounts.Default;
  @include Accounts.Container;

  .main.vertical-stack {
    gap: 0;
  }

  .main {
    > div {
      display: flex;
      flex-direction: column;
    }
    
    .button-container,
    .product-links {
      margin-top: Pathfinder.$space-500;
    }

    .margin-top-small {
      margin-top: Pathfinder.$space-150;
    }

    @include Pathfinder.below(min) {

      .button-container,
      .product-links {
        margin-top: Pathfinder.$space-400;
      }
    }
  }
}
</style>
